/**
 * Platform catalogue of org-level amenities shown on the public site sidebar.
 * Icons and customer-facing copy are presentation-only in packages/web.
 */
export const ORG_AMENITY_KEYS = [
  'parking',
  'cafe',
  'changing_rooms',
  'lockers',
  'wifi',
  'equipment_rental',
  'pro_shop',
  'accessible_access',
  'showers',
  'floodlights',
] as const;

export type OrgAmenityKey = (typeof ORG_AMENITY_KEYS)[number];

export const ORG_AMENITY_LABELS: Record<OrgAmenityKey, string> = {
  parking: 'Free parking',
  cafe: 'Café',
  changing_rooms: 'Changing rooms',
  lockers: 'Lockers',
  wifi: 'WiFi',
  equipment_rental: 'Equipment rental',
  pro_shop: 'Pro shop',
  accessible_access: 'Accessible access',
  showers: 'Showers',
  floodlights: 'Floodlights',
};

const AMENITY_SET = new Set<string>(ORG_AMENITY_KEYS);

export function isOrgAmenityKey(value: string): value is OrgAmenityKey {
  return AMENITY_SET.has(value);
}

/** Rejects unknown keys; returns deduplicated valid keys in catalogue order. */
export function validateOrgAmenities(values: unknown): OrgAmenityKey[] {
  if (!Array.isArray(values)) {
    return [];
  }
  const seen = new Set<string>();
  const result: OrgAmenityKey[] = [];
  for (const raw of values) {
    if (typeof raw !== 'string' || !isOrgAmenityKey(raw) || seen.has(raw)) {
      continue;
    }
    seen.add(raw);
    result.push(raw);
  }
  return ORG_AMENITY_KEYS.filter((key) => seen.has(key));
}
